/*
* ScaleBitmap
* Visit http://createjs.com/ for documentation, updates and examples.
*
* Copyright (c) 2010 gskinner.com, inc.
*
* Permission is hereby granted, free of charge, to any person
* obtaining a copy of this software and associated documentation
* files (the "Software"), to deal in the Software without
* restriction, including without limitation the rights to use,
* copy, modify, merge, publish, distribute, sublicense, and/or sell
* copies of the Software, and to permit persons to whom the
* Software is furnished to do so, subject to the following
* conditions:
*
* The above copyright notice and this permission notice shall be
* included in all copies or substantial portions of the Software.
*
* THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
* EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES
* OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
* NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
* HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
* WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
* FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR
* OTHER DEALINGS IN THE SOFTWARE.
*/

// namespace:
this.createjs = this.createjs||{};

(function() {
	"use strict";

// constructor:
  /**
   * A ScaleBitmap represents an Image, Canvas, or Video in the display list and is split into nine separate regions
   * to allow independent scaling of each region. This display object can be used to create scaling UI skins, such as
   * buttons and backgrounds with rounded corners. A ScaleBitmap can be instantiated using an existing HTML element,
   * or a string, similar to a Bitmap.
   *
   * <h4>Example</h4>
   *      var bitmap = new createjs.ScaleBitmap("imagePath.jpg", new createjs.Rectangle(14, 14, 3, 3));
   *      bitmap.setDrawSize(100, 100);
   *
   * Note: When a string path or image tag that is not yet loaded is used, the stage may need to be redrawn before it
   * will be displayed.
   *
   * @class ScaleBitmap
   * @extends DisplayObject
   * @constructor
   * @param {Image | HTMLCanvasElement | HTMLVideoElement | String} imageOrUri The source object or URI to an image to display. This can be either an Image, Canvas, or Video object, or a string URI to an image file to load and use. If it is a URI, a new Image object will be constructed and assigned to the .image property.
   * @param {Rectangle} scale9Grid The inner rectangle of the nine region grid.
   * @param {Rectangle} sourceRect sourceRect as bitmap.
   **/
	function ScaleBitmap(imageOrUri, scale9Grid, sourceRect) {
		this.DisplayObject_constructor();

	// public properties:
		/**
		 * The image to render. This can be an Image, a Canvas, or a Video.
		 * @property image
		 * @type Image | HTMLCanvasElement | HTMLVideoElement
		 **/
		if (typeof imageOrUri == "string") {
			this.image = new Image();
			this.image.src = imageOrUri;
		} else {
			this.image = imageOrUri;
		}

		/**
		 * Specifies the width of the drawn ScaleBitmap.
		 * @property drawWidth
		 * @type Number
		 * @default The original width of the image.
		 */
		this.drawWidth = sourceRect ? sourceRect.width : this.image.width;

		/**
		 * Specifies the height of the drawn ScaleBitmap.
		 * @property drawHeight
		 * @type Number
		 * @default The original height of the image.
		 */
		this.drawHeight = sourceRect ? sourceRect.height : this.image.height;
		/**
		 * Specifies the inner rectangle of the nine region scaling grid.
		 * @property scale9Grid
		 * @type Rectangle
		 */
		this.scale9Grid = scale9Grid;

		/**
		 * Whether or not the ScaleBitmap should be draw to the canvas at whole pixel coordinates.
		 * @property snapToPixel
		 * @type Boolean
		 * @default true
		 **/
		this.snapToPixel = true;

		this.sourceRect = sourceRect;
	}

	var p = createjs.extend(ScaleBitmap, createjs.DisplayObject);
	ScaleBitmap.prototype.constructor = ScaleBitmap;

	// static properties
	ScaleBitmap._colorTestCanvas = document.createElement("canvas");
	ScaleBitmap._colorTestContext = ScaleBitmap._colorTestCanvas.getContext("2d");
	ScaleBitmap._colorTestCanvas.width = ScaleBitmap._colorTestCanvas.height = 1;

// public methods:

	/**
	 * Changes the dimensions used the draw the ScaleBitmap.
	 *
	 * @method setDrawSize
	 * @param {Number} newWidth The new width of the drawn ScaleBitmap.
	 * @param {Number} newHeight The new height of the drawn ScaleBitmap.
	 */
	p.setDrawSize = function(newWidth, newHeight) {
		this.drawWidth = newWidth;
		this.drawHeight = newHeight;
	};

	/**
	 * Returns true or false indicating whether the display object would be visible if drawn to a canvas.
	 * This does not account for whether it would be visible within the boundaries of the stage.
	 * NOTE: This method is mainly for internal use, though it may be useful for advanced uses.
	 * @method isVisible
	 * @return {Boolean} Boolean indicating whether the display object would be visible if drawn to a canvas
	 **/
	p.isVisible = function() {
		var hasContent = this.cacheCanvas || (this.image && (this.image.complete || this.image.getContext || this.image.readyState >= 2));
		return !!(this.visible && this.alpha > 0 && this.scaleX != 0 && this.scaleY != 0 && hasContent);
	};

	/**
	 * Draws the display object into the specified context ignoring it's visible, alpha, shadow, and transform.
	 * Returns true if the draw was handled (useful for overriding functionality).
	 * NOTE: This method is mainly for internal use, though it may be useful for advanced uses.
	 * @method draw
	 * @param {CanvasRenderingContext2D} ctx The canvas 2D context object to draw into.
	 * @param {Boolean} ignoreCache Indicates whether the draw operation should ignore any current cache.
	 * For example, used for drawing the cache (to prevent it from simply drawing an existing cache back
	 * into itself).
	 **/
	p.draw = function(ctx, ignoreCache) {
		if (this.DisplayObject_draw(ctx, ignoreCache)) { return true; }

		// ScaleBitmap can be stand-alone, or it can belong to container
		// if it belongs to container, it should be treated in dependent mode
		var dependentMode = this.parent && this.parent.scale9Grid;

		var scale9Grid;
		var scaleX;
		var scaleY;

		if (dependentMode) {
			scale9Grid = this.parent.scale9Grid.clone();
			scale9Grid.offset(-this.x, -this.y);
			scaleX = this.scaleX * this.parent.scaleX;
			scaleY = this.scaleY * this.parent.scaleY;
		} else {
			scale9Grid = this.scale9Grid;
			scaleX = this.scaleX;
			scaleY = this.scaleY;
		}


		if (scale9Grid.width<=0 || scale9Grid.height<=0) {//if zero dimen exit prevents Ffox bug
			return true;
		}
		var imageWidth = this.sourceRect ? this.sourceRect.width : this.image.width;
		var imageHeight = this.sourceRect ? this.sourceRect.height : this.image.height;
		var sourceOffsetX = this.sourceRect ? this.sourceRect.x : 0, sourceOffsetY = this.sourceRect ? this.sourceRect.y : 0;
		var leftX = 0;
		var leftXScaled = leftX;
		var leftMiddleXScaled = leftXScaled;
		var leftWidth = scale9Grid.x;
		var leftWidthScaled = leftWidth / scaleX;

		var centerX = leftWidth;
		var centerXScaled = leftWidthScaled;
		var centerWidth = scale9Grid.width;
		if(centerWidth + centerX > imageWidth) centerWidth = imageWidth - centerWidth;

		var rightX = centerX + centerWidth;
		var rightWidth = this.drawWidth - rightX;
		var rightWidthScaled = rightWidth / scaleX;

		var centerWidthScaled = this.drawWidth - leftWidthScaled - rightWidthScaled;
		var rightXScaled = centerXScaled + centerWidthScaled;

		var topY = 0;
		var topYScaled = topY;
		var topHeight = scale9Grid.y;
		var topHeightScaled = topHeight / scaleY;

		var centerY = topHeight;
		var centerYScaled = topHeightScaled;
		var centerHeight = scale9Grid.height;
		if(centerHeight + centerY > imageHeight) centerHeight = imageHeight - centerY;

		var bottomY = centerY + centerHeight;
		var bottomHeight = this.drawHeight - bottomY;
		var bottomHeightScaled = bottomHeight / scaleY;

		var centerHeightScaled = this.drawHeight - topHeightScaled - bottomHeightScaled;
		var bottomYScaled = centerYScaled + centerHeightScaled;

		var compensate = this._sliceCompensate;
		if (isNaN(compensate)) {
			ctx.imageSmoothingEnabled = false;
			compensate = 1;
			var color;
			if(this.image.getContext) {
				color = this.image.getContext("2d").getImageData(Math.ceil(centerX + sourceOffsetX + centerWidth / 2 >> 0),Math.ceil(centerY + sourceOffsetY + centerHeight / 2 >> 0), 1, 1).data;
			} else {
				ScaleBitmap._colorTestContext.drawImage(this.image, Math.ceil(centerX + sourceOffsetX + centerWidth / 2 >> 0), Math.ceil(centerY + sourceOffsetY + centerHeight / 2 >> 0), 1, 1, 0, 0, 1, 1);
				color = ScaleBitmap._colorTestContext.getImageData(0, 0, 1, 1).data;
				ScaleBitmap._colorTestContext.clearRect(0, 0, 1, 1);
			}
			if(color && color[3] < 255) {
				// if it has alpha, we can't compensate that much
				compensate = currentBrowserInfo.isChrome ? 0 : 0.12;
			}
			this._sliceCompensate = compensate;
		}
		try{
			if(leftWidth && topHeight) ctx.drawImage(this.image, leftX + sourceOffsetX,   topY + sourceOffsetY, leftWidth,   topHeight, leftXScaled,   topYScaled, leftWidthScaled + compensate / scaleX,   topHeightScaled + compensate / scaleY);
			if(centerWidth && topHeight) ctx.drawImage(this.image, centerX + sourceOffsetX, topY + sourceOffsetY, centerWidth, topHeight, centerXScaled, topYScaled, centerWidthScaled + (compensate > rightWidthScaled? 0.1: compensate), topHeightScaled + compensate / scaleY);
			if(rightWidth && topHeight) ctx.drawImage(this.image, rightX + sourceOffsetX,  topY + sourceOffsetY, rightWidth,  topHeight, rightXScaled,  topYScaled, rightWidthScaled,  topHeightScaled + compensate / scaleY);
	
			if(leftWidth && centerHeight) ctx.drawImage(this.image, leftX + sourceOffsetX,   centerY + sourceOffsetY, leftWidth,   centerHeight, leftMiddleXScaled,   centerYScaled, leftWidthScaled + compensate / scaleX,   centerHeightScaled + (compensate > bottomHeightScaled? 0.1 : compensate / scaleY));
			if(centerWidth && centerHeight) ctx.drawImage(this.image, centerX + sourceOffsetX, centerY + sourceOffsetY, centerWidth, centerHeight, centerXScaled, centerYScaled, centerWidthScaled + (compensate > rightWidthScaled? 0.1: compensate), centerHeightScaled + (compensate > bottomHeightScaled? 0.1 : compensate / scaleY));
			if(rightWidth && centerHeight) ctx.drawImage(this.image, rightX + sourceOffsetX,  centerY + sourceOffsetY, rightWidth,  centerHeight, rightXScaled,  centerYScaled, rightWidthScaled,  centerHeightScaled + (compensate > bottomHeightScaled? 0.1 : compensate / scaleY));
	
			if(leftWidth && bottomHeight) ctx.drawImage(this.image, leftX + sourceOffsetX,   bottomY + sourceOffsetY, leftWidth,   bottomHeight, leftXScaled,   bottomYScaled, leftWidthScaled + compensate / scaleX,   bottomHeightScaled);
			if(centerWidth && bottomHeight) ctx.drawImage(this.image, centerX + sourceOffsetX, bottomY + sourceOffsetY, centerWidth, bottomHeight, centerXScaled, bottomYScaled, centerWidthScaled + (compensate > rightWidthScaled? 0.1: compensate), bottomHeightScaled);
			if(rightWidth && bottomHeight) ctx.drawImage(this.image, rightX + sourceOffsetX,  bottomY + sourceOffsetY, rightWidth,  bottomHeight, rightXScaled,  bottomYScaled, rightWidthScaled,  bottomHeightScaled);
		} catch(e) {
			//ignore edge strange error
		}

		return true;
	};

	p.setBounds = function(x, y, width, height) {
		this.DisplayObject_setBounds(x, y, width, height);
		this.drawWidth = width;
		this.drawHeight = height;
	};

	p.setScale9Grid = function(grid) {
		this.scale9Grid = grid;
	};

	/**
	 * Returns a clone of the ScaleBitmap instance.
	 * @method clone
	 * @return {ScaleBitmap} a clone of the ScaleBitmap instance.
	 **/
	p.clone = function() {
		var o = new ScaleBitmap(this.image, this.scale9Grid.clone());
		if (this.sourceRect) { o.sourceRect = this.sourceRect.clone(); }
		this.cloneProps(o);
		return o;
	};

	/**
	 * Returns a string representation of this object.
	 * @method toString
	 * @return {String} a string representation of the instance.
	 **/
	p.toString = function() {
		return "[ScaleBitmap (name="+  this.name +")]";
	};

	createjs.ScaleBitmap = createjs.promote(ScaleBitmap, "DisplayObject");

}());
